import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getEnumTKey, mapEnum } from "@/utils/strings";
import { PartnerData, PartnerStatus } from "@/pages/partners/types";
import {
  getPartnerTypeOptions,
  partnerStatusColors
} from "@/pages/partners/helpers";
import { useEntityFields } from "@/pages/settings/hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import TopFields, { FieldsConfig } from "@/components/layouts/TopFields";
import IconContainer from "@/components/ui/icon-container";
import { Bullet } from "@dreamhub/designsystem/icons";

interface Props {
  partner?: PartnerData;
  onUpdateField: (
    field: string,
    newValue: string | number | string[] | number[]
  ) => void;
  className?: string;
  itemClassName?: string;
}

const PartnerTopFields: React.FC<Props> = ({
  partner,
  onUpdateField,
  className,
  itemClassName
}) => {
  const { t } = useTranslation();

  const { data: fieldsResponse } = useEntityFields(EntityType.PARTNER);
  const typeOptions = useMemo(
    () => getPartnerTypeOptions(fieldsResponse?.fields || []),
    [fieldsResponse?.fields]
  );

  const statusOptions = useMemo(
    () =>
      mapEnum(PartnerStatus, (value: PartnerStatus) => ({
        id: value,
        displayName: t(
          getEnumTKey("partners", "statusValues", PartnerStatus, value)
        )
      })),
    [t]
  );

  const renderStatusIcon = (value: string | number) => (
    <IconContainer
      style={{ color: partnerStatusColors[Number(value) as PartnerStatus] }}
    >
      <Bullet />
    </IconContainer>
  );

  const fieldsConfig: FieldsConfig = {
    type: {
      type: "multiSelect",
      icon: "partnerType",
      iconColor: "primary",
      iconClassName: "mr-1",
      options: typeOptions,
      placeholder: t("partners.selectType"),
      popoverClassName: "max-w-[30vw]",
      onSelect: (values: string[]) =>
        onUpdateField(
          "type",
          values.map((value) => Number(value))
        )
    },
    status: {
      type: "select",
      options: statusOptions,
      renderOptionIcon: renderStatusIcon,
      onSelect: (value) => onUpdateField("status", Number(value))
    }
  };

  return (
    <TopFields
      entity={partner}
      fieldsConfig={fieldsConfig}
      onUpdateField={onUpdateField}
      className={className}
      itemClassName={itemClassName}
    />
  );
};

export default PartnerTopFields;
